import React, { PureComponent } from 'react';
import { bindActionCreators } from 'redux';
import { injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import {
    COUNTRY_CURRICULUM_ID,
    FILE_TYPES,
    GLOBAL_CURRICULUM_ID
} from '../../constants';

import { filterPublications } from '../curriculum-list/helpers';
import { convertByteToMegabyte } from '../../helpers/utils';

import {
    changeCountry,
    changeCurriculumType,
    changeDocument,
    changeProduct,
    changePublication,
    changeSection,
    changeState,
    getCountries,
    getDocumentsByPublication,
    getPublications,
    getSectionsByDocument,
    getStatesByCountry
} from '../curriculum-list/actions';
import { showToast } from '../../components/toast/actions';
import {
    checkSectionFile,
    getSectionFile,
    deleteSectionFile,
    uploadSectionFile
} from './actions';

import CurriculumDocuments from '../../components/curriculum-documents';

/**
 * CURRICULUM DOCUMENTS CONTAINER
 * ---------------------------------
 * Manages the provided state, dispatch and function props to components/curriculum-documents
 * Uses containers/curriculum-list/reducer to manage data to avoid major refactor
 */
class CurriculumDocumentsContainer extends PureComponent {
    static propTypes = {
        changeCountry: PropTypes.func,
        changeCurriculumType: PropTypes.func,
        changeDocument: PropTypes.func,
        changePublication: PropTypes.func,
        changeSection: PropTypes.func,
        changeState: PropTypes.func,
        changeProduct: PropTypes.func,
        checkSectionFile: PropTypes.func,
        countries: PropTypes.array,
        countryLoaded: PropTypes.bool,
        curriculumAlignmentDocuments: PropTypes.object,
        curriculumType: PropTypes.string,
        deleteSectionFile: PropTypes.func,
        documents: PropTypes.array,
        getCountries: PropTypes.func,
        getDocumentsByPublication: PropTypes.func,
        getPublications: PropTypes.func,
        getSectionFile: PropTypes.func,
        getSectionsByDocument: PropTypes.func,
        getStatesByCountry: PropTypes.func,
        isRequesting: PropTypes.bool,
        isUploadingFile: PropTypes.bool,
        publications: PropTypes.array,
        sections: PropTypes.array,
        selectedCountry: PropTypes.string,
        selectedDocument: PropTypes.string,
        selectedProductId: PropTypes.number,
        selectedPublication: PropTypes.string,
        selectedSection: PropTypes.string,
        selectedState: PropTypes.string,
        showToast: PropTypes.func,
        states: PropTypes.array,
        uploadSectionFile: PropTypes.func
    }

    constructor(props) {
        super(props);

        this.fileUploadRef = React.createRef();

        this.maxUploadFileSizeInMegabytes = 5;

        this.state = {
            uploadedFile: null,
            fileContent: null
        };

        this.handleCountryOnChange = this.handleCountryOnChange.bind(this);
        this.handleCurriculumTypeOnChange = this.handleCurriculumTypeOnChange.bind(this);
        this.handleFileUpload = this.handleFileUpload.bind(this);
        this.handlePublicationOnChange = this.handlePublicationOnChange.bind(this);
    }

    componentDidMount() {
        const { countryLoaded } = this.props;

        if (!countryLoaded) {
            this.props.getCountries();
        }
    }

    componentDidUpdate(prevProps) {
        this.manageProductOrPublicationChange({ prevProps });
    }

    manageProductOrPublicationChange = ({ prevProps }) => {
        const { selectedSection } = this.props;
        const { selectedSection: prevSelectedSection } = prevProps;

        if (selectedSection !== prevSelectedSection) {
            this.manageCheckSection({ selectedSection });
        }
    }

    /**
     * CHECK PUBLICATION
     */
    manageCheckSection = ({ selectedSection }) => {
        if (selectedSection) {
            this.props.checkSectionFile(selectedSection);
        }
    }

    /**
     * DROPDOWN CHANGE HANDLERS
     */
    handleCurriculumTypeOnChange = (collectionName, key) => {
        this.props.changeCurriculumType(key);
        if (key === GLOBAL_CURRICULUM_ID || key === COUNTRY_CURRICULUM_ID) {
            this.props.getPublications({ accessStatus: key });
        }
    }

    handleCountryOnChange = (collectionName, key) => {
        this.props.changeCountry(key);
        this.props.getStatesByCountry(key);
    };

    handlePublicationOnChange = (collectionName, key) => {
        this.props.changePublication(key);
        this.props.getDocumentsByPublication(key);
    };

    handleStateOnChange = (collectionName, key) => {
        this.props.changeState(key);
    };

    handleDocumentOnChange = (collectionName, key) => {
        this.props.changeDocument(key);
        this.props.getSectionsByDocument(key);
    }

    handleSectionOnChange = (collectionName, key) => {
        const updatedProductId = this.props.sections.find(item => item.key === key).productId || 0;

        this.props.changeSection(key);
        this.props.changeProduct(updatedProductId);
    }

    /**
     * FILE UPLOAD
     */
    handleFileUploadError = (isPdfFile, isValidSize) => {
        if (!isPdfFile) {
            this.props.showToast('File is not a valid PDF.', 'error');
        }
        if (!isValidSize) {
            this.props.showToast('File exceeds 5MB size limit.', 'error');
        }
    }

    validateFileUpload = ({ uploadedFile }) => {
        const { PDF } = FILE_TYPES;
        const isPdfFile = uploadedFile.type === PDF;
        const fileSizeInMegabytes = convertByteToMegabyte({
            bytes: uploadedFile.size
        });
        const isValidSize = fileSizeInMegabytes < this.maxUploadFileSizeInMegabytes;
        const isValidFile = isPdfFile && isValidSize;

        return {
            isValidSize,
            isPdfFile,
            isValidFile
        };
    }

    handleFileUpload = (event) => {
        const uploadedFile = event.target.files[0];
        const {
            isPdfFile,
            isValidFile,
            isValidSize
        } = this.validateFileUpload({
            uploadedFile
        });

        if (isValidFile) {
            this.setState({
                uploadedFile
            });
        } else {
            this.handleFileUploadError(isPdfFile, isValidSize);
        }
    }

    resetFileUpload = () => {
        this.setState(({
            uploadedFile: null
        }));
        this.fileUploadRef.current.value = null;
    }

    onUploadFile = () => {
        const { uploadedFile } = this.state;
        const { selectedSection } = this.props;

        this.props.uploadSectionFile(selectedSection, uploadedFile);
        this.resetFileUpload();
    }

    onDeleteFile = () => {
        const { selectedSection } = this.props;

        this.props.deleteSectionFile(selectedSection);
    }

    onDownloadFile = () => {
        const { selectedSection } = this.props;

        this.props.getSectionFile(selectedSection);
    }

    render() {
        const publications = filterPublications({
            publications: this.props.publications,
            selectedCountry: this.props.selectedCountry,
            selectedState: this.props.selectedState
        });

        return (
            <CurriculumDocuments
                countries={this.props.countries}
                curriculumAlignmentDocuments={this.props.curriculumAlignmentDocuments}
                curriculumType={this.props.curriculumType}
                documents={this.props.documents}
                fileUploadRef={this.fileUploadRef}
                handleCountryOnChange={this.handleCountryOnChange}
                handleCurriculumTypeOnChange={this.handleCurriculumTypeOnChange}
                handleDocumentOnChange={this.handleDocumentOnChange}
                handleFileUpload={this.handleFileUpload}
                handlePublicationOnChange={this.handlePublicationOnChange}
                handleSectionOnChange={this.handleSectionOnChange}
                handleStateOnChange={this.handleStateOnChange}
                isRequesting={this.props.isRequesting}
                isUploadingFile={this.props.isUploadingFile}
                onDeleteFile={this.onDeleteFile}
                onDownloadFile={this.onDownloadFile}
                onUploadFile={this.onUploadFile}
                publications={publications}
                sections={this.props.sections}
                selectedCountry={this.props.selectedCountry}
                selectedDocument={this.props.selectedDocument}
                selectedProductId={this.props.selectedProductId}
                selectedPublication={this.props.selectedPublication}
                selectedSection={this.props.selectedSection}
                selectedState={this.props.selectedState}
                states={this.props.states}
                uploadedFile={this.state.uploadedFile}
            />
        );
    }
}

function mapStateToProps(state) {
    return {
        countries: state.curriculumList.countries,
        countryLoaded: state.curriculumList.countryLoaded,
        curriculumAlignmentDocuments: state.curriculumList.curriculumAlignmentDocuments,
        curriculumType: state.curriculumList.curriculumType,
        documents: state.curriculumList.documents.map(item => ({
            key: item.id,
            value: `${item.name} (${item.adoptionYear})`
        })),
        isRequesting: state.curriculumList.isRequesting,
        isUploadingFile: state.curriculumList.isUploadingFile,
        publications: state.curriculumList.publications,
        sections: state.curriculumList.sections.map(item => ({
            key: item.id,
            value: item.name,
            productId: item.subjectId
        })),
        selectedCountry: state.curriculumList.selectedCountry,
        selectedDocument: state.curriculumList.selectedDocument,
        selectedProductId: state.curriculumList.selectedProductId,
        selectedPublication: state.curriculumList.selectedPublication,
        selectedSection: state.curriculumList.selectedSection,
        selectedState: state.curriculumList.selectedState,
        states: state.curriculumList.states
    };
}

function mapDispatchToProps(dispatch) {
    return bindActionCreators({
        changeCountry,
        changeCurriculumType,
        changeDocument,
        changeProduct,
        changePublication,
        changeSection,
        changeState,
        checkSectionFile,
        deleteSectionFile,
        getCountries,
        getDocumentsByPublication,
        getPublications,
        getSectionFile,
        getSectionsByDocument,
        getStatesByCountry,
        uploadSectionFile,
        showToast
    }, dispatch);
}

export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(CurriculumDocumentsContainer));



// WEBPACK FOOTER //
// ./app/containers/curriculum-documents/index.js